<template>
  <div class="create-order-panel">
    <Card :bordered="false" class="page-card">
      <div class="page-header">
        <div>
          <h2>{{ pageTitle }}</h2>
          <p>{{ isEditMode ? '修改当前 T6 运单信息，保存后同步刷新订单列表。' : '创建 T6 运单，可保存为草稿或提交预报。' }}</p>
        </div>
        <Input
          v-model="scanInput"
          class="scan-input"
          search
          clearable
          placeholder="扫描客户参考号、系统单号、运单号、转单号、FBAID、标签单号"
          @on-enter="handleScan"
          @on-search="handleScan"
        />
      </div>
    </Card>

    <Form ref="orderForm" :model="form" :label-width="110" label-position="right" class="order-form">
      <Card title="运单信息" class="section-card">
        <Row :gutter="16">
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="客户参考号">
              <Input v-model="form.waybillInfo.customerRef" />
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="运单号">
              <Input v-model="form.waybillInfo.waybillNumber" disabled />
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="系统单号">
              <Input v-model="form.waybillInfo.systemNumber" disabled />
            </FormItem>
          </Col>

          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="收货渠道" required>
              <Select v-model="form.waybillInfo.channelId" filterable clearable placeholder="请选择">
                <Option v-for="ch in availableChannels" :key="ch.channelCode" :value="ch.channelCode">
                  {{ ch.channelName }}
                </Option>
              </Select>
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="目的地" required>
              <Select v-model="form.waybillInfo.destination" filterable clearable placeholder="请选择">
                <Option v-for="c in countries" :key="c.code" :value="c.code">{{ c.code }} - {{ c.name }}</Option>
              </Select>
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="总件数" required>
              <InputNumber v-model="form.waybillInfo.pieces" :min="1" class="full-input" />
            </FormItem>
          </Col>

          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="预报实重" required>
              <InputNumber v-model="form.waybillInfo.forecastWeight" :min="0" :step="0.01" class="full-input" />
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="预报材重">
              <InputNumber v-model="form.waybillInfo.forecastMWeight" :min="0" :step="0.01" class="full-input" />
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="预报方数">
              <InputNumber v-model="form.waybillInfo.forecastSquare" :min="0" :step="0.001" class="full-input" />
            </FormItem>
          </Col>

          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="包裹类型">
              <Select v-model="form.waybillInfo.packageType" clearable>
                <Option value="package">包裹</Option>
                <Option value="document">文件</Option>
                <Option value="pallet">托盘</Option>
                <Option value="PAK">PAK袋</Option>
              </Select>
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="货物类型">
              <Select v-model="form.waybillInfo.cargoType" clearable>
                <Option value="general">普货</Option>
                <Option value="sensitive">敏感货</Option>
                <Option value="food">食品</Option>
                <Option value="liquid">液体</Option>
                <Option value="WPX">WPX</Option>
                <Option value="SPX">SPX</Option>
                <Option value="FPX">FPX</Option>
                <Option value="LPX">LPX</Option>
              </Select>
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="运单申报币别">
              <Select v-model="form.waybillInfo.currency" clearable>
                <Option value="CNY">CNY</Option>
                <Option value="USD">USD</Option>
                <Option value="AUD">AUD</Option>
                <Option value="EUR">EUR</Option>
                <Option value="GBP">GBP</Option>
              </Select>
            </FormItem>
          </Col>

          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="报关类型">
              <Input v-model="form.waybillInfo.declareType" />
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="物品类别">
              <Input v-model="form.waybillInfo.goodsCategory" />
            </FormItem>
          </Col>
          <Col :xs="24" :md="12" :lg="8">
            <FormItem label="附加选项">
              <div class="inline-controls">
                <Checkbox v-model="form.waybillInfo.splitMaterial">分摊到材积</Checkbox>
                <Checkbox v-model="form.waybillInfo.insured">是否投保</Checkbox>
                <Checkbox v-model="form.waybillInfo.hasBattery">是否带电</Checkbox>
              </div>
            </FormItem>
          </Col>

          <Col :xs="24">
            <FormItem label="运单备注">
              <Input v-model="form.waybillInfo.note" type="textarea" :autosize="{ minRows: 2, maxRows: 4 }" />
            </FormItem>
          </Col>
        </Row>
      </Card>

      <Card class="section-card">
        <div slot="title" class="card-title-row">
          <span>材积信息</span>
          <Checkbox v-model="form.waybillInfo.useDimensions" @on-change="syncDimensionTotals">根据材积统计件数和重量</Checkbox>
        </div>
        <Table :columns="dimensionColumns" :data="form.dimensions" border size="small">
          <template slot="dimQty" slot-scope="{ index }">
            <InputNumber v-model="form.dimensions[index].qty" :min="1" class="table-input" @on-change="syncDimensionTotals" />
          </template>
          <template slot="dimLength" slot-scope="{ index }">
            <InputNumber v-model="form.dimensions[index].length" :min="0" :step="0.1" class="table-input" @on-change="syncDimensionTotals" />
          </template>
          <template slot="dimWidth" slot-scope="{ index }">
            <InputNumber v-model="form.dimensions[index].width" :min="0" :step="0.1" class="table-input" @on-change="syncDimensionTotals" />
          </template>
          <template slot="dimHeight" slot-scope="{ index }">
            <InputNumber v-model="form.dimensions[index].height" :min="0" :step="0.1" class="table-input" @on-change="syncDimensionTotals" />
          </template>
          <template slot="dimWeight" slot-scope="{ index }">
            <InputNumber v-model="form.dimensions[index].weight" :min="0" :step="0.01" class="table-input" @on-change="syncDimensionTotals" />
          </template>
          <template slot="dimAction" slot-scope="{ index }">
            <Button type="text" icon="ios-copy-outline" @click="copyDimension(index)" />
            <Button type="text" icon="ios-trash-outline" @click="removeDimension(index)" />
          </template>
        </Table>
        <div class="table-actions">
          <Button size="small" icon="md-add" @click="addDimension">添加材积</Button>
          <Button size="small" icon="ios-trash-outline" @click="clearDimensions">清空材积</Button>
        </div>
      </Card>

      <Row :gutter="16">
        <Col :xs="24" :lg="12">
          <Card title="收件信息" class="section-card">
            <Row :gutter="16">
              <Col :xs="24" :md="12">
                <FormItem label="名称" required>
                  <Input v-model="form.consignee.name" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="12">
                <FormItem label="电话" required>
                  <Input v-model="form.consignee.phone" />
                </FormItem>
              </Col>
              <Col :xs="24">
                <FormItem label="地址1" required>
                  <Input v-model="form.consignee.address1" />
                </FormItem>
              </Col>
              <Col :xs="24">
                <FormItem label="地址2">
                  <Input v-model="form.consignee.address2" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="8">
                <FormItem label="邮编" required>
                  <Input v-model="form.consignee.postcode" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="8">
                <FormItem label="城市" required>
                  <Input v-model="form.consignee.city" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="8">
                <FormItem label="省州" required>
                  <Input v-model="form.consignee.province" />
                </FormItem>
              </Col>
            </Row>
            <div class="common-address">
              <Checkbox v-model="saveRecipient">保存为常用收件人</Checkbox>
            </div>
          </Card>
        </Col>

        <Col :xs="24" :lg="12">
          <Card title="寄件信息" class="section-card">
            <Row :gutter="16">
              <Col :xs="24" :md="12">
                <FormItem label="名称">
                  <Input v-model="form.sender.name" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="12">
                <FormItem label="国家" required>
                  <Select v-model="form.sender.countryCode" filterable clearable>
                    <Option v-for="c in countries" :key="c.code" :value="c.code">{{ c.code }} - {{ c.name }}</Option>
                  </Select>
                </FormItem>
              </Col>
              <Col :xs="24" :md="12">
                <FormItem label="公司">
                  <Input v-model="form.sender.company" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="12">
                <FormItem label="电话">
                  <Input v-model="form.sender.phone" />
                </FormItem>
              </Col>
              <Col :xs="24">
                <FormItem label="地址1">
                  <Input v-model="form.sender.address1" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="8">
                <FormItem label="邮编">
                  <Input v-model="form.sender.postcode" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="8">
                <FormItem label="城市">
                  <Input v-model="form.sender.city" />
                </FormItem>
              </Col>
              <Col :xs="24" :md="8">
                <FormItem label="省州">
                  <Input v-model="form.sender.province" />
                </FormItem>
              </Col>
            </Row>
            <div class="common-address">
              <Checkbox v-model="saveSender">保存为常用寄件人</Checkbox>
            </div>
          </Card>
        </Col>
      </Row>

      <Card class="section-card">
        <div slot="title" class="card-title-row">
          <span>物品信息</span>
          <strong class="declare-total">申报价值合计：{{ declareTotalText }}</strong>
        </div>
        <Table :columns="goodsColumns" :data="form.goods" border size="small">
          <template slot="goodsSku" slot-scope="{ index }">
            <Input v-model="form.goods[index].sku" size="small" />
          </template>
          <template slot="goodsEnName" slot-scope="{ index }">
            <Input v-model="form.goods[index].enName" size="small" />
          </template>
          <template slot="goodsCnName" slot-scope="{ index }">
            <Input v-model="form.goods[index].cnName" size="small" />
          </template>
          <template slot="goodsNetWeight" slot-scope="{ index }">
            <InputNumber v-model="form.goods[index].netWeight" :min="0" :step="0.01" class="table-input" />
          </template>
          <template slot="goodsQty" slot-scope="{ index }">
            <InputNumber v-model="form.goods[index].qty" :min="1" class="table-input" @on-change="calcItemTotal(form.goods[index])" />
          </template>
          <template slot="goodsUnitPrice" slot-scope="{ index }">
            <InputNumber v-model="form.goods[index].unitPrice" :min="0" :step="0.01" class="table-input" @on-change="calcItemTotal(form.goods[index])" />
          </template>
          <template slot="goodsTotalValue" slot-scope="{ index }">
            <InputNumber v-model="form.goods[index].totalValue" :min="0" :step="0.01" class="table-input" />
          </template>
          <template slot="goodsCurrency" slot-scope="{ index }">
            <Select v-model="form.goods[index].currency" size="small">
              <Option value="USD">USD</Option>
              <Option value="CNY">CNY</Option>
              <Option value="AUD">AUD</Option>
              <Option value="EUR">EUR</Option>
              <Option value="GBP">GBP</Option>
            </Select>
          </template>
          <template slot="goodsHsCode" slot-scope="{ index }">
            <Input v-model="form.goods[index].hsCode" size="small" />
          </template>
          <template slot="goodsMaterial" slot-scope="{ index }">
            <Input v-model="form.goods[index].material" size="small" />
          </template>
          <template slot="goodsRemark" slot-scope="{ index }">
            <Input v-model="form.goods[index].remark" size="small" />
          </template>
          <template slot="goodsAction" slot-scope="{ index }">
            <Button type="text" icon="ios-copy-outline" @click="copyGoods(index)" />
            <Button type="text" icon="ios-trash-outline" @click="removeGoods(index)" />
          </template>
        </Table>
        <div class="table-actions">
          <Button size="small" icon="md-add" @click="addGoods">添加物品</Button>
          <Button size="small" icon="ios-trash-outline" @click="clearGoods">清空物品</Button>
        </div>
      </Card>
    </Form>

    <div class="order-footer">
      <div class="footer-save">
        <span>保存到：</span>
        <RadioGroup v-model="form.status">
          <Radio label="Draft">草稿</Radio>
          <Radio label="Predicted">已预报</Radio>
        </RadioGroup>
      </div>
      <div class="footer-actions">
        <Button type="primary" :loading="saving" @click="saveAndContinue">保存并继续</Button>
        <Button type="success" :loading="saving" @click="saveAndClose">保存并关闭</Button>
        <Button @click="handleClose">关闭</Button>
      </div>
      <div class="footer-print">
        <Button icon="ios-print-outline" size="small">打印机设置</Button>
        <InputNumber v-model="printOptions.count" :min="1" size="small" style="width:70px" />
        <Checkbox v-model="printOptions.preview">浏览打印</Checkbox>
        <Checkbox v-model="printOptions.paperless">无纸化提交</Checkbox>
      </div>
    </div>
  </div>
</template>

<script>
import printingApi from '@/api/printing'

const EMPTY_DIMENSION = { qty: 1, length: 0, width: 0, height: 0, weight: 0 }
const EMPTY_GOODS = {
  sku: '',
  cnName: '',
  enName: '',
  qty: 1,
  netWeight: 0,
  unitPrice: 0,
  totalValue: 0,
  currency: 'USD',
  hsCode: '',
  material: '',
  remark: ''
}

export default {
  name: 'CreateOrderPanel',
  props: {
    channelCode: String,
    channels: { type: Array, default: () => [] },
    mode: { type: String, default: 'create' },
    orderId: [String, Number],
    orderDetail: { type: Object, default: null }
  },
  data() {
    return {
      scanInput: '',
      saving: false,
      importing: false,
      saveRecipient: false,
      saveSender: false,
      localChannels: [],
      printOptions: {
        count: 1,
        preview: false,
        paperless: false
      },
      form: this.emptyForm(),
      countries: [
        { code: 'AU', name: '澳大利亚' },
        { code: 'NZ', name: '新西兰' },
        { code: 'US', name: '美国' },
        { code: 'GB', name: '英国' },
        { code: 'CA', name: '加拿大' },
        { code: 'JP', name: '日本' },
        { code: 'KR', name: '韩国' },
        { code: 'SG', name: '新加坡' },
        { code: 'MY', name: '马来西亚' },
        { code: 'TH', name: '泰国' },
        { code: 'DE', name: '德国' },
        { code: 'FR', name: '法国' },
        { code: 'IT', name: '意大利' },
        { code: 'ES', name: '西班牙' },
        { code: 'NL', name: '荷兰' },
        { code: 'CN', name: '中国' }
      ]
    }
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit'
    },
    pageTitle() {
      return this.isEditMode ? '修改订单-标准格式' : '创建订单'
    },
    availableChannels() {
      if (this.channels && this.channels.length > 0) return this.channels
      return this.localChannels
    },
    declareTotal() {
      return this.form.goods.reduce((sum, item) => sum + Number(item.totalValue || 0), 0)
    },
    declareTotalText() {
      return this.declareTotal ? this.declareTotal.toFixed(2) : '0.00'
    },
    dimensionColumns() {
      return [
        { title: '件数', slot: 'dimQty', minWidth: 90, renderHeader: h => this.requiredHeader(h, '件数') },
        { title: '长', slot: 'dimLength', minWidth: 90, renderHeader: h => this.requiredHeader(h, '长') },
        { title: '宽', slot: 'dimWidth', minWidth: 90, renderHeader: h => this.requiredHeader(h, '宽') },
        { title: '高', slot: 'dimHeight', minWidth: 90, renderHeader: h => this.requiredHeader(h, '高') },
        { title: '单件实重', slot: 'dimWeight', minWidth: 110, renderHeader: h => this.requiredHeader(h, '单件实重') },
        { title: '操作', slot: 'dimAction', width: 100, align: 'center' }
      ]
    },
    goodsColumns() {
      return [
        { title: '商品SKU', slot: 'goodsSku', minWidth: 120 },
        { title: '英文名', slot: 'goodsEnName', minWidth: 130, renderHeader: h => this.requiredHeader(h, '英文名') },
        { title: '中文名', slot: 'goodsCnName', minWidth: 130, renderHeader: h => this.requiredHeader(h, '中文名') },
        { title: '净重', slot: 'goodsNetWeight', minWidth: 110, renderHeader: h => this.requiredHeader(h, '净重') },
        { title: '单箱数量', slot: 'goodsQty', minWidth: 110, renderHeader: h => this.requiredHeader(h, '单箱数量') },
        { title: '申报单价', slot: 'goodsUnitPrice', minWidth: 110, renderHeader: h => this.requiredHeader(h, '申报单价') },
        { title: '单箱申报总值', slot: 'goodsTotalValue', minWidth: 130, renderHeader: h => this.requiredHeader(h, '单箱申报总值') },
        { title: '申报币别', slot: 'goodsCurrency', minWidth: 110, renderHeader: h => this.requiredHeader(h, '申报币别') },
        { title: '海关编码', slot: 'goodsHsCode', minWidth: 120, renderHeader: h => this.requiredHeader(h, '海关编码') },
        { title: '材质(英文)', slot: 'goodsMaterial', minWidth: 130, renderHeader: h => this.requiredHeader(h, '材质(英文)') },
        { title: '备注', slot: 'goodsRemark', minWidth: 150 },
        { title: '操作', slot: 'goodsAction', width: 100, align: 'center', fixed: 'right' }
      ]
    }
  },
  watch: {
    channelCode(val) {
      if (!this.isEditMode && val && this.form.waybillInfo.channelId !== val) {
        this.form.waybillInfo.channelId = val
        this.form.waybillInfo.printChannel = val
      }
    },
    mode(val) {
      if (val === 'create') {
        this.resetForm(this.channelCode)
      } else if (this.orderDetail) {
        this.applyOrderDetail(this.orderDetail)
      } else if (this.orderId) {
        this.loadOrderForEdit(this.orderId)
      }
    },
    orderId(val) {
      if (this.isEditMode && val) this.loadOrderForEdit(val)
    },
    orderDetail: {
      deep: true,
      handler(val) {
        if (this.isEditMode && val) this.applyOrderDetail(val)
      }
    }
  },
  mounted() {
    this.loadChannelsIfNeeded()
    if (this.isEditMode) {
      if (this.orderDetail) this.applyOrderDetail(this.orderDetail)
      else if (this.orderId) this.loadOrderForEdit(this.orderId)
    } else {
      this.resetForm(this.channelCode)
    }
  },
  methods: {
    requiredHeader(h, label) {
      return h('span', [label, h('span', { class: 'required-star' }, ' *')])
    },
    emptyForm() {
      return {
        status: 'Draft',
        meta: {
          id: null,
          pkid: '',
          systemNumber: '',
          waybillNumber: '',
          oldStatus: '',
          formatPkId: '12',
          editFormatType: '2',
          editformatpkid: '12',
          clickStatus: ''
        },
        waybillInfo: {
          customerRef: '',
          channelId: this.channelCode || '',
          printChannel: this.channelCode || '',
          destination: '',
          pieces: 1,
          packageType: 'package',
          forecastWeight: 0,
          forecastMWeight: 0,
          forecastSquare: 0,
          splitMaterial: false,
          cargoType: 'general',
          declareType: '',
          insured: false,
          hasBattery: false,
          goodsCategory: '',
          currency: 'CNY',
          note: '',
          useDimensions: false,
          systemNumber: '',
          waybillNumber: ''
        },
        consignee: {
          name: '',
          phone: '',
          postcode: '',
          province: '',
          city: '',
          address1: '',
          address2: ''
        },
        sender: {
          name: '',
          company: '',
          phone: '',
          postcode: '',
          province: '',
          city: '',
          address1: '',
          countryCode: 'CN'
        },
        dimensions: [{ ...EMPTY_DIMENSION }],
        goods: [{ ...EMPTY_GOODS }]
      }
    },
    async loadChannelsIfNeeded() {
      if (this.channels && this.channels.length > 0) return
      try {
        const res = await printingApi.listChannels()
        this.localChannels = res.items || []
      } catch (e) {
        this.$Message.error('加载渠道失败')
      }
    },
    async loadOrderForEdit(id) {
      if (!id) return
      try {
        const detail = await printingApi.getOrder(id)
        this.applyOrderDetail(detail)
      } catch (e) {
        this.$Message.error((e.response && e.response.data && e.response.data.detail) || '加载订单详情失败')
      }
    },
    applyOrderDetail(detail) {
      const form = this.emptyForm()
      const info = detail.waybillInfo || {}
      const summary = detail.summary || {}
      form.status = detail.status || summary.status || 'Draft'
      form.meta = {
        id: detail.id,
        pkid: detail.pkid || '',
        systemNumber: info.systemNumber || summary.systemNumber || '',
        waybillNumber: info.waybillNumber || summary.waybillNumber || '',
        oldStatus: info.oldStatus || detail.status || summary.status || '',
        formatPkId: info.formatPkId || '12',
        editFormatType: info.editFormatType || '2',
        editformatpkid: info.editformatpkid || info.formatPkId || '12',
        clickStatus: ''
      }
      form.waybillInfo = {
        ...form.waybillInfo,
        ...info,
        channelId: summary.channelCode || this.channelCode || info.localChannelCode || info.channelCode || info.channelId || '',
        printChannel: info.printChannel || info.channelId || summary.outChannelId || summary.channelCode || this.channelCode || '',
        packageType: this.normalizePackageType(info.packageType),
        cargoType: this.normalizeCargoType(info.cargoType || summary.goodsType),
        systemNumber: info.systemNumber || summary.systemNumber || '',
        waybillNumber: info.waybillNumber || summary.waybillNumber || ''
      }
      form.consignee = { ...form.consignee, ...(detail.consignee || {}) }
      form.sender = { ...form.sender, ...(detail.sender || {}) }
      form.dimensions = (detail.dimensions && detail.dimensions.length) ? detail.dimensions.map(d => ({ ...EMPTY_DIMENSION, ...d })) : [{ ...EMPTY_DIMENSION }]
      form.goods = (detail.goods && detail.goods.length) ? detail.goods.map(g => ({ ...EMPTY_GOODS, ...g })) : [{ ...EMPTY_GOODS }]
      this.form = form
    },
    applyCommonDefaults() {
      const recipient = this.readStorage('redspider.commonRecipient')
      const sender = this.readStorage('redspider.commonSender')
      if (recipient) this.form.consignee = { ...this.form.consignee, ...recipient }
      if (sender) this.form.sender = { ...this.form.sender, ...sender }
    },
    readStorage(key) {
      try {
        return JSON.parse(localStorage.getItem(key) || 'null')
      } catch (e) {
        return null
      }
    },
    writeCommonAddresses() {
      if (this.saveRecipient) {
        localStorage.setItem('redspider.commonRecipient', JSON.stringify(this.form.consignee))
      }
      if (this.saveSender) {
        localStorage.setItem('redspider.commonSender', JSON.stringify(this.form.sender))
      }
    },
    handleScan() {
      const value = (this.scanInput || '').trim()
      if (!value) return
      if (!this.form.waybillInfo.customerRef) {
        this.form.waybillInfo.customerRef = value
      }
      this.scanInput = ''
    },
    addDimension() {
      this.form.dimensions.push({ ...EMPTY_DIMENSION })
    },
    copyDimension(index) {
      this.form.dimensions.splice(index + 1, 0, { ...this.form.dimensions[index] })
      this.syncDimensionTotals()
    },
    removeDimension(index) {
      if (this.form.dimensions.length === 1) {
        this.form.dimensions.splice(0, 1, { ...EMPTY_DIMENSION })
      } else {
        this.form.dimensions.splice(index, 1)
      }
      this.syncDimensionTotals()
    },
    clearDimensions() {
      this.form.dimensions = [{ ...EMPTY_DIMENSION }]
      this.syncDimensionTotals()
    },
    syncDimensionTotals() {
      const dims = this.validDimensions()
      const totalPieces = dims.reduce((sum, dim) => sum + Number(dim.qty || 0), 0)
      const totalWeight = dims.reduce((sum, dim) => sum + Number(dim.qty || 0) * Number(dim.weight || 0), 0)
      const totalVolume = dims.reduce((sum, dim) => {
        return sum + Number(dim.qty || 0) * Number(dim.length || 0) * Number(dim.width || 0) * Number(dim.height || 0) / 1000000
      }, 0)
      if (this.form.waybillInfo.useDimensions) {
        this.form.waybillInfo.pieces = totalPieces || 1
        this.form.waybillInfo.forecastWeight = Number(totalWeight.toFixed(3))
        this.form.waybillInfo.forecastMWeight = Number(totalVolume.toFixed(3))
        this.form.waybillInfo.forecastSquare = Number(totalVolume.toFixed(3))
      }
    },
    addGoods() {
      this.form.goods.push({ ...EMPTY_GOODS })
    },
    copyGoods(index) {
      this.form.goods.splice(index + 1, 0, { ...this.form.goods[index] })
    },
    removeGoods(index) {
      if (this.form.goods.length === 1) {
        this.form.goods.splice(0, 1, { ...EMPTY_GOODS })
      } else {
        this.form.goods.splice(index, 1)
      }
    },
    clearGoods() {
      this.form.goods = [{ ...EMPTY_GOODS }]
    },
    calcItemTotal(item) {
      item.totalValue = Number(((item.qty || 1) * (item.unitPrice || 0)).toFixed(2))
    },
    validDimensions() {
      return this.form.dimensions.filter(dim => {
        return Number(dim.length || 0) || Number(dim.width || 0) || Number(dim.height || 0) || Number(dim.weight || 0)
      })
    },
    validGoods() {
      return this.form.goods.filter(item => {
        return item.sku || item.cnName || item.enName || item.hsCode || item.material || item.remark || Number(item.netWeight || 0) || Number(item.unitPrice || 0) || Number(item.totalValue || 0)
      })
    },
    normalizePackageType(value) {
      const text = String(value || '').trim()
      const key = text.toLowerCase()
      const map = {
        package: 'package',
        g: 'package',
        '包裹': 'package',
        document: 'document',
        d: 'document',
        '文件': 'document',
        pallet: 'pallet',
        p: 'pallet',
        '托盘': 'pallet',
        pak: 'PAK',
        'pak袋': 'PAK'
      }
      return map[key] || map[text] || 'package'
    },
    normalizeCargoType(value) {
      const text = String(value || '').trim()
      const key = text.toLowerCase()
      const map = {
        general: 'general',
        wpx: 'general',
        '普货': 'general',
        '普通货': 'general',
        '包裹': 'general',
        sensitive: 'sensitive',
        spx: 'sensitive',
        '敏感': 'sensitive',
        '敏感货': 'sensitive',
        food: 'food',
        fpx: 'food',
        '食品': 'food',
        liquid: 'liquid',
        lpx: 'liquid',
        '液体': 'liquid'
      }
      return map[key] || map[text] || 'general'
    },
    buildPayload() {
      const info = this.form.waybillInfo
      const localChannelCode = info.channelId || this.channelCode || ''
      return {
        channel_code: localChannelCode,
        status: this.form.status,
        meta: this.form.meta,
        waybillInfo: {
          customerRef: info.customerRef,
          destination: info.destination,
          printChannel: info.printChannel || info.channelId,
          pieces: info.pieces || 1,
          packageType: this.normalizePackageType(info.packageType),
          forecastWeight: info.forecastWeight || 0,
          forecastVolume: info.forecastMWeight || 0,
          forecastCbm: info.forecastSquare || 0,
          cargoType: this.normalizeCargoType(info.cargoType),
          declareType: info.declareType || '',
          goodsCategory: info.goodsCategory || '',
          currency: info.currency || 'CNY',
          insured: !!info.insured,
          hasBattery: !!info.hasBattery,
          note: info.note || ''
        },
        consignee: {
          name: this.form.consignee.name,
          phone: this.form.consignee.phone,
          postcode: this.form.consignee.postcode,
          province: this.form.consignee.province,
          city: this.form.consignee.city,
          address1: this.form.consignee.address1,
          address2: this.form.consignee.address2
        },
        sender: {
          name: this.form.sender.name,
          company: this.form.sender.company,
          phone: this.form.sender.phone,
          postcode: this.form.sender.postcode,
          province: this.form.sender.province,
          city: this.form.sender.city,
          address1: this.form.sender.address1,
          address2: '',
          countryCode: this.form.sender.countryCode || 'CN'
        },
        dimensions: this.validDimensions().map(dim => ({
          qty: dim.qty || 1,
          length: dim.length || 0,
          width: dim.width || 0,
          height: dim.height || 0,
          weight: dim.weight || 0
        })),
        goods: this.validGoods().map(item => ({
          sku: item.sku,
          enName: item.enName,
          cnName: item.cnName,
          netWeight: item.netWeight || 0,
          qty: item.qty || 1,
          unitPrice: item.unitPrice || 0,
          totalValue: item.totalValue || 0,
          currency: item.currency || 'USD',
          hsCode: item.hsCode,
          material: item.material,
          remark: item.remark
        }))
      }
    },
    validate() {
      const info = this.form.waybillInfo
      if (!info.channelId) return this.warnInvalid('请选择收货渠道')
      if (!info.destination) return this.warnInvalid('请选择目的地')
      if (!Number(info.pieces || 0)) return this.warnInvalid('请填写总件数')
      if (!Number(info.forecastWeight || 0)) return this.warnInvalid('请填写预报实重')
      if (!this.form.consignee.name) return this.warnInvalid('请填写收件人名称')
      if (!this.form.consignee.address1) return this.warnInvalid('请填写收件地址1')
      if (!this.form.consignee.postcode) return this.warnInvalid('请填写收件人邮编')
      if (!this.form.consignee.city) return this.warnInvalid('请填写收件人城市')
      if (!this.form.consignee.province) return this.warnInvalid('请填写收件人省州')
      if (!this.form.consignee.phone) return this.warnInvalid('请填写收件人电话')
      if (!this.form.sender.countryCode) return this.warnInvalid('请选择寄件人国家')

      const dims = this.validDimensions()
      for (let i = 0; i < dims.length; i += 1) {
        const dim = dims[i]
        if (!Number(dim.qty || 0) || !Number(dim.length || 0) || !Number(dim.width || 0) || !Number(dim.height || 0) || !Number(dim.weight || 0)) {
          return this.warnInvalid(`请补全第 ${i + 1} 行材积信息`)
        }
      }

      const goods = this.validGoods()
      if (!goods.length) return this.warnInvalid('请至少填写一行物品信息')
      for (let i = 0; i < goods.length; i += 1) {
        const item = goods[i]
        if (!item.enName || !item.cnName || !Number(item.netWeight || 0) || !Number(item.qty || 0) || !Number(item.unitPrice || 0) || !Number(item.totalValue || 0) || !item.currency || !item.hsCode || !item.material) {
          return this.warnInvalid(`请补全第 ${i + 1} 行物品必填信息`)
        }
      }
      return true
    },
    warnInvalid(message) {
      this.$Message.warning(message)
      return false
    },
    resetForm(channelCode) {
      this.form = this.emptyForm()
      this.form.waybillInfo.channelId = channelCode || this.channelCode || ''
      this.form.waybillInfo.printChannel = channelCode || this.channelCode || ''
      this.scanInput = ''
      this.applyCommonDefaults()
    },
    async save(closeAfterSave) {
      this.syncDimensionTotals()
      if (!this.validate()) return
      this.saving = true
      try {
        const payload = this.buildPayload()
        const channelCode = payload.channel_code
        const result = this.isEditMode && this.form.meta.id
          ? await printingApi.updateOrder(this.form.meta.id, payload)
          : await printingApi.createOrder(payload)
        this.writeCommonAddresses()
        this.$Message.success(this.form.status === 'Draft' ? '已保存为草稿' : '已提交预报')
        this.$emit('saved', channelCode, result, { mode: this.mode, closeAfterSave })
        if (!closeAfterSave && !this.isEditMode) {
          this.resetForm(channelCode)
        }
      } catch (e) {
        this.$Message.error((e.response && e.response.data && e.response.data.detail) || '保存失败')
      } finally {
        this.saving = false
      }
    },
    saveAndContinue() {
      this.save(false)
    },
    saveAndClose() {
      this.save(true)
    },
    handleClose() {
      this.$emit('closed')
    },
    async handleImportFile(file) {
      const channelCode = this.form.waybillInfo.channelId
      if (!channelCode) {
        this.$Message.warning('请先选择收货渠道')
        return false
      }
      this.importing = true
      try {
        const res = await printingApi.importOrders(channelCode, this.form.status || 'Draft', file)
        this.$Message.success(`导入完成，成功 ${res.success || 0} 条，失败 ${res.failed || 0} 条`)
      } catch (e) {
        this.$Message.error((e.response && e.response.data && e.response.data.detail) || '导入失败')
      } finally {
        this.importing = false
      }
      return false
    }
  }
}
</script>

<style scoped>
.create-order-panel {
  min-height: 100%;
  padding: 12px;
  background: #f5f7f9;
  padding-bottom: 72px;
}

.page-card,
.section-card {
  margin-bottom: 12px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #17233d;
}

.page-header p {
  margin: 6px 0 0;
  color: #808695;
}

.scan-input {
  width: 420px;
}

.order-form >>> .ivu-card-head {
  padding: 10px 16px;
}

.order-form >>> .ivu-card-body {
  padding: 16px;
}

.full-input,
.table-input {
  width: 100%;
}

.inline-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.declare-total,
.required-star {
  color: #ed4014;
}

.table-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.common-address {
  margin-left: 110px;
  color: #808695;
}

.order-footer {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  margin: 12px -12px -72px;
  padding: 10px 16px;
  background: #fff;
  border-top: 1px solid #e8eaec;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
}

.footer-save,
.footer-actions,
.footer-print {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.footer-actions {
  justify-content: center;
}

.footer-print {
  justify-content: flex-end;
}

@media (max-width: 1280px) {
  .page-header,
  .order-footer {
    grid-template-columns: 1fr;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .scan-input {
    width: 100%;
  }

  .order-footer {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    position: static;
    margin-bottom: -72px;
  }

  .footer-print {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>
